import React from 'react';
import { Link } from 'react-router-dom';
import { Code2, Zap, GitBranch, Search, FileText, CheckCircle, AlertTriangle, Network, BookOpen, ListChecks } from 'lucide-react';

function Landing() {
  const features = [
    {
      icon: <AlertTriangle className="w-8 h-8 text-[#f59e0b]" />,
      title: "Change Impact Predictor",
      description: "If you modify this file, 12 other components may be affected",
      detail: "Visual blast radius on flow chart shows exactly what breaks before you commit"
    },
    {
      icon: <BookOpen className="w-8 h-8 text-[#3b82f6]" />,
      title: "Knowledge Base",
      description: "Auto-generate docs from code + your annotations",
      detail: "Never write documentation again - it updates automatically with your code"
    },
    {
      icon: <Search className="w-8 h-8 text-[#8b5cf6]" />,
      title: "Intelligent Search",
      description: "\"Show me all authentication code\"",
      detail: "Natural language search understands your codebase context"
    },
    {
      icon: <Network className="w-8 h-8 text-[#10b981]" />,
      title: "Impact Analysis",
      description: "\"What will break if I change this?\"",
      detail: "See dependency chains and affected components instantly"
    },
    {
      icon: <ListChecks className="w-8 h-8 text-[#ec4899]" />,
      title: "Custom Workflows",
      description: "Checklist for code review, deployment, etc.",
      detail: "Create templates for common tasks and never miss a step"
    },
    {
      icon: <GitBranch className="w-8 h-8 text-[#06b6d4]" />,
      title: "Visual Code Map",
      description: "Interactive flow charts of your entire codebase",
      detail: "Understand complex architectures at a glance"
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      {/* Navigation */}
      <nav className="border-b border-[#1e293b] bg-[#0a0a0f]/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Code2 className="w-8 h-8 text-[#3b82f6]" />
              <span className="text-xl font-bold bg-gradient-to-r from-white to-[#94a3b8] bg-clip-text text-transparent">
                TriggerDocs.dev
              </span>
            </div>
            <div className="flex items-center gap-4">
              <Link
                to="/login"
                className="text-[#cbd5e1] hover:text-white transition-colors"
              >
                Sign In
              </Link>
              <Link
                to="/signup"
                className="px-4 py-2 bg-gradient-to-r from-[#3b82f6] to-[#2563eb] hover:from-[#2563eb] hover:to-[#1d4ed8] rounded-lg font-medium transition-all transform hover:scale-105"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Animated background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#3b82f6]/10 via-transparent to-[#8b5cf6]/10 animate-pulse"></div>

        {/* Grid pattern overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_110%)] opacity-20"></div>

        <div className="relative max-w-7xl mx-auto px-6 py-24">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1e293b]/50 rounded-full border border-[#334155] mb-8">
              <span className="text-sm text-[#60a5fa]">Understand AI-Generated Code Instantly</span>
            </div>

            <h1 className="text-6xl font-bold mb-6 text-white">
              Make Sense of Modern Development
            </h1>

            <p className="text-xl text-[#94a3b8] mb-12 max-w-2xl mx-auto">
              TriggerDocs helps developers and product managers understand complex codebases created by LLMs. Get instant visualizations, flowcharts, and comprehensive summaries.
            </p>

            <div className="flex items-center justify-center gap-4">
              <Link
                to="/signup"
                className="px-8 py-4 bg-gradient-to-r from-[#3b82f6] to-[#2563eb] hover:from-[#2563eb] hover:to-[#1d4ed8] rounded-lg font-semibold text-lg transition-all transform hover:scale-105 shadow-lg shadow-[#3b82f6]/20"
              >
                Start Analyzing
              </Link>
              <a
                href="#features"
                className="px-8 py-4 bg-[#1e293b] hover:bg-[#334155] rounded-lg font-semibold text-lg transition-colors border border-[#334155]"
              >
                See Features
              </a>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 mt-20 max-w-3xl mx-auto">
              <div className="text-center">
                <div className="text-4xl font-bold text-[#3b82f6] mb-2">12+</div>
                <div className="text-sm text-[#94a3b8]">Dependencies Tracked</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-[#8b5cf6] mb-2">95%</div>
                <div className="text-sm text-[#94a3b8]">Breaking Changes Prevented</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-[#10b981] mb-2">3x</div>
                <div className="text-sm text-[#94a3b8]">Faster Code Reviews</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-gradient-to-b from-[#0a0a0f] to-[#0f172a]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-white to-[#94a3b8] bg-clip-text text-transparent">
              Powerful Features
            </h2>
            <p className="text-xl text-[#94a3b8] max-w-2xl mx-auto">
              Everything you need to understand and maintain complex codebases
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-[#0f172a]/80 backdrop-blur-xl border border-[#1e293b] rounded-2xl p-8 hover:border-[#334155] transition-all group hover:scale-105"
              >
                <div className="mb-4 transform group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3 text-white">{feature.title}</h3>
                <p className="text-[#3b82f6] mb-3 font-medium italic">
                  "{feature.description}"
                </p>
                <p className="text-[#94a3b8] text-sm">
                  {feature.detail}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-white to-[#94a3b8] bg-clip-text text-transparent">
              How It Works
            </h2>
            <p className="text-xl text-[#94a3b8]">
              Get insights in 3 simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-[#3b82f6] to-[#2563eb] rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold shadow-lg shadow-[#3b82f6]/20">
                1
              </div>
              <h3 className="text-xl font-semibold mb-3">Upload Your Code</h3>
              <p className="text-[#94a3b8]">
                Drag and drop your codebase or connect your Git repository
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-[#8b5cf6] to-[#7c3aed] rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold shadow-lg shadow-[#8b5cf6]/20">
                2
              </div>
              <h3 className="text-xl font-semibold mb-3">AI Analysis</h3>
              <p className="text-[#94a3b8]">
                Our AI maps dependencies, generates docs, and identifies patterns
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-[#10b981] to-[#059669] rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold shadow-lg shadow-[#10b981]/20">
                3
              </div>
              <h3 className="text-xl font-semibold mb-3">Get Insights</h3>
              <p className="text-[#94a3b8]">
                Visualize, search, and predict impact of every change
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-b from-[#0f172a] to-[#0a0a0f]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Understand Your Code?
          </h2>
          <p className="text-xl text-[#94a3b8] mb-8">
            Join developers who ship with confidence
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#3b82f6] to-[#2563eb] hover:from-[#2563eb] hover:to-[#1d4ed8] rounded-lg font-semibold text-lg transition-all transform hover:scale-105 shadow-lg shadow-[#3b82f6]/20"
          >
            Get Started Free
            <Zap className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#1e293b] py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-[#64748b]">
              <Code2 className="w-5 h-5" />
              <span>© 2025 TriggerDocs.dev</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-[#64748b]">
              <a href="#" className="hover:text-white transition-colors">Privacy</a>
              <a href="#" className="hover:text-white transition-colors">Terms</a>
              <a href="#" className="hover:text-white transition-colors">Docs</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Landing;
